// packages/api/src/PublicVolunteerService.ts
//
// PUBLIC volunteer sign-up client (#A4.6) — the anonymous writes behind the volunteer-shifts block, and the
// volunteer's own "my volunteering" view. Runs through the injected client so it works in the BROWSER.
//
// ⚠ Notice what the inputs do NOT carry: no status, no approval mode, no event id. Which shifts are open, how many
// places are left and whether a sign-up is confirmed on the spot are read from the SLOTS by the server.
import { gql } from "@apollo/client";
import { OpResult } from "@sdflc/api-helpers";

import { ApiService } from "./ApiService";
import { ANSWER_IMAGE_FIELDS, QUESTION_IMAGE_FIELDS, executeMutationImageSafe, executeQueryImageSafe, imageSafeDocs } from "./answerImageFields";
import type { PublicVolunteerShift, PublicVolunteerShiftReward } from "./PublicEventService";
import type { PublicCoupon, PublicRsvpAnswerInput, PublicRsvpFormQuestion, PublicRsvpMineAnswer } from "./PublicRsvpService";
import { refusedNewInputs } from "./refusedNewInputs";

/** The sign-up form: the organizer's questions and the shifts open right now. */
export interface PublicVolunteerSignUpForm {
  eventId?: string | null;
  questions?: PublicRsvpFormQuestion[] | null;
  shifts?: PublicVolunteerShift[] | null;
}

export interface PublicVolunteerSignUpInput {
  url: string;
  /** ⚠ REQUIRED — mint it with `ensureVisitorId()`: a sign-up binds this browser. */
  visitorId: string;
  firstName: string;
  lastName?: string | null;
  /** Required — the way back from another browser is a code sent here. */
  email: string;
  phone?: string | null;
  message?: string | null;
  slotIds: string[];
  /** May be omitted when only ADDING shifts to this browser's existing sign-up. */
  answers?: PublicRsvpAnswerInput[] | null;
}

/** One requested shift on the volunteer's own sign-up. */
export interface PublicVolunteerMyShift {
  /** The requested-shift id — what withdraw takes. */
  id?: string | null;
  slotId?: string | null;
  name?: string | null;
  roleName?: string | null;
  startsAt?: string | null;
  endsAt?: string | null;
  locationKind?: string | null;
  locationLabel?: string | null;
  /** Where to come, read live: the venue's or point of interest's name (the event's venue when the shift names none). */
  placeName?: string | null;
  /** Its one-line address. */
  placeAddress?: string | null;
  /** The place's pin — both or neither. */
  latitude?: number | null;
  longitude?: number | null;
  /** The pin's coverage circle, in metres; null without one. */
  radiusMeters?: number | null;
  /** Google Maps directions (to the pin, else the address); null when there is nowhere to go. */
  directionsUrl?: string | null;
  /** pending | approved | declined | withdrawn. */
  status?: string | null;
  withdrawnByApplicant?: boolean | null;
  /** ⚠ Only on a CONFIRMED shift — private until then. */
  instructions?: string | null;
  checkedInAt?: string | null;
  rewards?: PublicVolunteerShiftReward[] | null;
  /** ⚠ The SERVER's answer — never re-derived in the browser. */
  canWithdraw?: boolean | null;
  /** #A4.7 — what the shift pays per hour, in the application's `currencyCode`; null = an unpaid volunteer shift. */
  hourlyRate?: number | null;
  /** #A4.7 — on a confirmed shift: expected | actual | no_show (| unpaid, when only a bonus is paid); null otherwise. */
  payStatus?: string | null;
  /** #A4.7 — billable minutes (scheduled or worked, less the break, rounded, at least the minimum). */
  payMinutes?: number | null;
  /** #A4.7 — the pay expected for the shift (before bonus) while it is not checked in AND out yet. */
  expectedPay?: number | null;
  /** #A4.7 — the pay for the time actually worked (before bonus). */
  actualPay?: number | null;
  /** #A4.7 — a bonus on this shift, and why. */
  bonusAmount?: number | null;
  bonusNote?: string | null;
}

/** A volunteer's OWN sign-up. */
export interface PublicVolunteerMyApplication {
  id?: string | null;
  firstName?: string | null;
  lastName?: string | null;
  email?: string | null;
  phone?: string | null;
  message?: string | null;
  submittedAt?: string | null;
  answers?: PublicRsvpMineAnswer[] | null;
  shifts?: PublicVolunteerMyShift[] | null;
  /** #A4.7 — the event's currency: every pay amount on the shifts is in it. */
  currencyCode?: string | null;
  /** Their check-in code (the event guest code) — a QR once a shift is confirmed; null before. */
  guestCode?: string | null;
  /** Rewards issued so far (at check-in), in the shape `GuestCouponsPanel` renders. */
  coupons?: PublicCoupon[] | null;
  /** Wallet link bases (null until configured) — the same ones the RSVP / ticket rails publish. */
  walletAppleUrl?: string | null;
  walletGoogleUrl?: string | null;
}

export interface PublicVolunteerUpdateMineInput {
  url: string;
  visitorId: string;
  applicationId: string;
  firstName?: string | null;
  lastName?: string | null;
  phone?: string | null;
  message?: string | null;
  answers?: PublicRsvpAnswerInput[] | null;
}

const SHIFT_FIELDS = `
  id name volunteerRoleId roleName roleSummary roleResponsibilities roleEligibility roleColor roleIcon
  startsAt endsAt locationKind locationLabel latitude longitude
  quantityNeeded spotsLeft isFull approvalMode applicationsCloseAt
  rewards { name description color icon quantity }
`;

/** The volunteer-visible fields, selected identically by every call that returns a sign-up. */
const MINE_FIELDS = `
  id firstName lastName email phone message submittedAt
  answers { questionId valueText valueNumber valueBoolean valueDate optionIds valueAddress { streetAddress extraAddress city stateProvince postalCode country } ${ANSWER_IMAGE_FIELDS} }
  shifts {
    id slotId name roleName startsAt endsAt locationKind locationLabel placeName placeAddress latitude longitude radiusMeters directionsUrl
    status withdrawnByApplicant instructions checkedInAt canWithdraw
    rewards { name description color icon quantity }
    hourlyRate payStatus payMinutes expectedPay actualPay bonusAmount bonusNote
  }
  currencyCode
  guestCode
  coupons {
    id code couponTypeId name description color icon couponValue
    items { name quantity }
    couponStatus redeemedCount maxRedemptions issuedToLabel guestName eventTimeSlotId dateLabel dateStartsAt redeemAt
  }
  walletAppleUrl walletGoogleUrl
`;

const FORM_QUERY = imageSafeDocs(`
  query PublicVolunteerSignUpForm($params: PublicVolunteerFormInput) {
    publicVolunteerSignUpForm(params: $params) {
      code
      data {
        eventId
        questions {
          id questionType valueType title helpText placeholder
          isRequired requiredScope personField ${QUESTION_IMAGE_FIELDS} askOncePerGroup
          conditionMatch orderNo
          options { id label value price }
        }
        shifts { ${SHIFT_FIELDS} }
      }
      errors { name errors warnings }
    }
  }
`);

const mineOp = (kind: "query" | "mutation", name: string, input: string) => imageSafeDocs(`
  ${kind} PublicVolunteer${name}($params: ${input}) {
    publicVolunteer${name}(params: $params) {
      code
      data { ${MINE_FIELDS} }
      errors { name errors warnings }
    }
  }
`);

const MINE_QUERY = mineOp("query", "Mine", "PublicVolunteerMineInput");
const SIGN_UP_MUTATION = mineOp("mutation", "SignUp", "PublicVolunteerSignUpInput");
const UPDATE_MINE_MUTATION = mineOp("mutation", "UpdateMine", "PublicVolunteerUpdateMineInput");
const WITHDRAW_MINE_MUTATION = mineOp("mutation", "WithdrawMine", "PublicVolunteerWithdrawMineInput");
const FORGET_MINE_MUTATION = mineOp("mutation", "ForgetMine", "PublicVolunteerMineInput");
const REDEEM_LINK_MUTATION = mineOp("mutation", "RedeemLink", "PublicVolunteerRedeemLinkInput");
const REDEEM_CODE_MUTATION = mineOp("mutation", "RedeemCode", "PublicVolunteerRedeemCodeInput");

const REQUEST_CODE_MUTATION = gql`
  mutation PublicVolunteerRequestCode($params: PublicVolunteerRequestCodeInput) {
    publicVolunteerRequestCode(params: $params) {
      code
      data { sent }
      errors { name errors warnings }
    }
  }
`;

export class PublicVolunteerService {
  /** The organizer's questions + the shifts open now. Its own query — not part of every page payload. */
  /** `visitorId` (2026-10-04): on an invite-only event the form is its guests' only — sent only when there is one. */
  static async signUpForm(url: string, options: Record<string, any> = {}, visitorId: string | null = null): Promise<OpResult> {
    const vid = (visitorId ?? "").trim();

    const opts = { fetchPolicy: "network-only", ...options };
    const result = await executeQueryImageSafe(FORM_QUERY, { params: { url, ...(vid ? { visitorId: vid } : {}) } }, opts);

    // An API / gateway deployed after the site does not know `visitorId` here yet — read the form as before.
    return vid && refusedNewInputs(result, ["visitorId"]) ? executeQueryImageSafe(FORM_QUERY, { params: { url } }, opts) : result;
  }

  /**
   * Sign up for shifts, or ADD shifts to this browser's sign-up. Refusals are keyed `slot:<id>` (to the shift),
   * `email`, `answers` or `slotIds` — put each at its input.
   */
  static async signUp(params: PublicVolunteerSignUpInput): Promise<OpResult> {
    return executeMutationImageSafe(SIGN_UP_MUTATION, { params });
  }

  /** This browser's sign-ups for the event. An unknown browser gets [] — never an error. */
  static async mine(url: string, visitorId: string | null, options: Record<string, any> = {}): Promise<OpResult> {
    return executeQueryImageSafe(MINE_QUERY, { params: { url, visitorId: visitorId || null } }, { fetchPolicy: "network-only", ...options });
  }

  /** Name / phone / message / answers. The email is not editable — it is the way back. */
  static async updateMine(params: PublicVolunteerUpdateMineInput): Promise<OpResult> {
    return executeMutationImageSafe(UPDATE_MINE_MUTATION, { params });
  }

  /** Withdraw from requested shifts (their `id`s). Refusals are keyed `shift:<id>`. */
  static async withdrawMine(url: string, visitorId: string, shiftIds: string[]): Promise<OpResult> {
    return executeMutationImageSafe(WITHDRAW_MINE_MUTATION, { params: { url, visitorId, shiftIds } });
  }

  /** Unbind this browser. Nothing is deleted; the way back is the emailed code — say so before calling. */
  static async forgetMine(url: string, visitorId: string): Promise<OpResult> {
    return executeMutationImageSafe(FORGET_MINE_MUTATION, { params: { url, visitorId } });
  }

  /** Adopt a sign-up from the signed link in its emails (`?volunteer=`). */
  static async redeemLink(url: string, token: string, visitorId: string): Promise<OpResult> {
    return executeMutationImageSafe(REDEEM_LINK_MUTATION, { params: { url, token, visitorId } });
  }

  /** ⚠ NEUTRAL: the same answer whether the address matched or not — never report "not found". */
  static async requestCode(url: string, email: string): Promise<OpResult> {
    return ApiService.executeMutation(REQUEST_CODE_MUTATION, { params: { url, email } });
  }

  /** Redeem the emailed code on this browser — it adopts the sign-up. Single use. */
  static async redeemCode(url: string, code: string, visitorId: string): Promise<OpResult> {
    return executeMutationImageSafe(REDEEM_CODE_MUTATION, { params: { url, code, visitorId } });
  }
}
